// page.tsx
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { useVendorData } from './hoooks/useVendorData'; // 경로 주의
import { useCategoryData } from './hoooks/useCategoryData'; // 경로 주의
import { VendorSearchModal } from './components/vendorSearchModal'; // 경로 주y
import { VendorFormModal } from './components/vendorForModal'; // 경로 주y
import { type Vendor, type WedlogBizVendor, type VendorFormSubmitPayload } from './types'; // 경로 주의

interface InviteFormData {
  vendorIdx: string;
  name: string;
  categoryName: string;
  contact: string;
  managerName: string;
  email: string;
  businessNumber: string;
  address: string;
}

type InvitePrecheckMode = 'UPGRADE_LOCAL' | 'LINK_EXISTING' | 'ALREADY_PARTNERED' | 'REVIEW_REQUIRED';
type InviteActionMode = 'UPGRADE_LOCAL' | 'LINK_EXISTING';

interface InvitePrecheckResponse {
  mode: InvitePrecheckMode;
  message?: string;
  confirmToken?: string;
  expiresInSeconds?: number;
  inviteRequestIdx?: number;
}

interface InviteConfirmResponse {
  mode: InviteActionMode;
  message?: string;
  partnerVendorIdx?: number;
  rollbackToken?: string;
  rollbackExpiresInSeconds?: number;
  issuedTempPassword?: string;
}

interface InviteRollbackInfo {
  rollbackToken: string;
  mode: InviteActionMode;
  message: string;
  rollbackExpiresInSeconds: number;
  partnerVendorIdx?: number;
  inviteRequestIdx?: number;
}

const onlyDigits = (value: string) => value.replace(/\D/g, '').slice(0, 11);

const formatMobile = (value: string) => {
  const digits = onlyDigits(value);
  if (digits.length <= 3) return digits;
  if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7, 11)}`;
};

const isValidKoreanMobile010 = (value: string) => /^010-\d{4}-\d{4}$/.test(value);
const getInviteModeLabel = (mode: InviteActionMode) =>
  mode === 'UPGRADE_LOCAL' ? '신규 협력업체 등록' : '기존 업체 연결';
const getPrecheckGuideMessage = (mode: InviteActionMode) =>
  mode === 'UPGRADE_LOCAL'
    ? '기존 가입 이력이 확인되지 않아 신규 협력업체로 등록됩니다.'
    : '기존 가입 업체를 우리 업체의 협력 파트너로 연결합니다.';
const sanitizeInviteMessage = (message?: string) => {
  if (!message) return '';
  return message
    .replace(/confirm 호출 시/gi, '초대 확정 시')
    .replace(/local 업체/gi, '미가입 협력업체')
    .replace(/로컬 업체/gi, '미가입 협력업체')
    .replace(/guest로 승격됩니다/gi, '협력 파트너로 등록됩니다')
    .replace(/guest/gi, '협력 파트너');
};

export default function VendorManagement() {
  const navigate = useNavigate();

  // --- Custom Hooks (Logic) ---
  const { vendors, isLoading, fetchVendors, createVendor, linkExistingVendor, updateVendorStatus, deleteVendor } = useVendorData();
  const { categoryList, categories, fetchCategories } = useCategoryData();
  
  // --- UI State ---
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilterIdx, setCategoryFilterIdx] = useState<number | null>(null);
  const [selectedParentCategoryIdx, setSelectedParentCategoryIdx] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState('전체');
  const [showAddForm, setShowAddForm] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [selectedWedlogVendor, setSelectedWedlogVendor] = useState<WedlogBizVendor | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [isInvitingVendor, setIsInvitingVendor] = useState(false);
  const [isRollingBackInvite, setIsRollingBackInvite] = useState(false);
  const [inviteRollbackInfo, setInviteRollbackInfo] = useState<InviteRollbackInfo | null>(null);
  const [inviteFormData, setInviteFormData] = useState<InviteFormData>({
    vendorIdx: '',
    name: '',
    categoryName: '',
    contact: '',
    managerName: '',
    email: '',
    businessNumber: '',
    address: ''
  });

  // --- Effects ---
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchVendors(statusFilter, searchTerm, categoryFilterIdx);
    }, 300);
    return () => clearTimeout(timer);
  }, [fetchVendors, statusFilter, searchTerm, categoryFilterIdx]);

  // --- Helpers ---
  const parentCategoryOptions = useMemo(() => {
    return categoryList.map((parent) => ({
      label: parent.name,
      value: parent.vendorCategoryIdx
    }));
  }, [categoryList]);
  const selectedParentCategory = useMemo(
    () => categoryList.find((parent) => parent.vendorCategoryIdx === selectedParentCategoryIdx),
    [categoryList, selectedParentCategoryIdx]
  );
  const childCategoryOptions = selectedParentCategory?.children || [];
  const statusOptions = ['전체', '활성', '중단'];
  const rollbackAvailableMinutes = inviteRollbackInfo
    ? Math.ceil(inviteRollbackInfo.rollbackExpiresInSeconds / 60)
    : 0;
  const getVendorRelationBadge = (vendor: Vendor) => {
    if (vendor.isPartnership) {
      return { label: '연결 업체', className: 'bg-orange-100 text-orange-700' };
    }
    if (vendor.canInvite) {
      return { label: '초대 전 업체', className: 'bg-slate-100 text-slate-700' };
    }
    return { label: '초대 완료 업체', className: 'bg-emerald-100 text-emerald-700' };
  };
  const isRollbackTargetVendor = (vendorId: number | string) => {
    if (!inviteRollbackInfo?.rollbackToken || inviteRollbackInfo.partnerVendorIdx == null) return false;
    return Number(vendorId) === Number(inviteRollbackInfo.partnerVendorIdx);
  };
  const isRollbackTargetVisible = inviteRollbackInfo
    ? vendors.some((vendor) => isRollbackTargetVendor(vendor.id))
    : false;

  // --- Handlers ---
  const handleVendorCreateSuccess = async (payload: VendorFormSubmitPayload) => {
    const { selectedExistingHostIdx, newVendorForm } = payload;
    let success = false;

    if (selectedExistingHostIdx) {
      success = await linkExistingVendor(selectedExistingHostIdx);
    } else if (newVendorForm) {
      success = await createVendor(newVendorForm);
    } else {
      alert('등록 데이터가 없습니다. 다시 시도해주세요.');
      return false;
    }

    if (success) fetchVendors(statusFilter, searchTerm, categoryFilterIdx); // Refresh
    return success;
  };

  const handleVendorDelete = async (vendor: Vendor, e: React.MouseEvent) => {
    e.stopPropagation();
    const isPartnership = vendor.isPartnership;
    const confirmMessage = isPartnership
      ? '해당 업체와의 협력 연결을 해제하시겠습니까?'
      : '정말 삭제하시겠습니까? (복구 불가)';
    if (!confirm(confirmMessage)) return;

    const success = await deleteVendor(vendor.id);
    if (success) {
      alert(isPartnership ? '업체 연결이 해제되었습니다.' : '업체가 삭제되었습니다.');
      fetchVendors(statusFilter, searchTerm, categoryFilterIdx);
    }
  };

  const handleRelationshipStatusChange = async (vendor: Vendor, e: React.MouseEvent) => {
    e.stopPropagation();
    const nextStatus = vendor.status === '활성' ? 'INACTIVE' : 'ACTIVE';
    const nextStatusLabel = nextStatus === 'ACTIVE' ? '활성' : '중단';
    const shouldProceed = confirm(`연결 업체 상태를 "${nextStatusLabel}"으로 변경하시겠습니까?`);
    if (!shouldProceed) return;

    const success = await updateVendorStatus(vendor.id, nextStatus);
    if (success) {
      alert('상태가 변경되었습니다.');
      fetchVendors(statusFilter, searchTerm, categoryFilterIdx);
    }
  };

  const handleSelectAllCategories = () => {
    setSelectedParentCategoryIdx(null);
    setCategoryFilterIdx(null);
  };

  const handleSelectParentCategory = (parentCategoryIdx: number) => {
    setSelectedParentCategoryIdx(parentCategoryIdx);
    setCategoryFilterIdx(parentCategoryIdx);
  };

  const handleSelectChildCategory = (childCategoryIdx: number | null) => {
    if (!selectedParentCategoryIdx) return;
    if (childCategoryIdx === null) {
      setCategoryFilterIdx(selectedParentCategoryIdx);
      return;
    }
    setCategoryFilterIdx(childCategoryIdx);
  };

  const handleOpenInviteModal = (vendor: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setInviteFormData({
      vendorIdx: String(vendor.id || ''),
      name: vendor.name || '',
      categoryName: vendor.subCategory ? `${vendor.category} > ${vendor.subCategory}` : (vendor.category || '미지정'),
      contact: formatMobile(vendor.contact || ''),
      managerName: vendor.manager || '',
      email: vendor.email || '',
      businessNumber: vendor.businessNumber || '',
      address: vendor.address || ''
    });
    setShowInviteModal(true);
  };

  const handleInviteVendor = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isInvitingVendor) return;

    if (!inviteFormData.vendorIdx) {
      alert('업체 식별 정보가 없습니다. 목록에서 다시 시도해주세요.');
      return;
    }
    if (!isValidKoreanMobile010(inviteFormData.contact)) {
      alert('대표 연락처는 010-xxxx-xxxx 형식만 입력 가능합니다. (예: 010-1234-5678)');
      return;
    }

    setIsInvitingVendor(true);
    try {
      const token = localStorage.getItem('accessToken');
      const precheckPayload = {
        vendorIdx: Number(inviteFormData.vendorIdx),
        contact: formatMobile(inviteFormData.contact),
        ...(inviteFormData.managerName.trim() ? { managerName: inviteFormData.managerName.trim() } : {}),
        ...(inviteFormData.email.trim() ? { email: inviteFormData.email.trim() } : {})
      };

      const precheckResponse = await fetch('https://api.wedmin.biz/vendor/invite/precheck', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(precheckPayload)
      });

      const precheckResult = await precheckResponse.json().catch(() => null);
      const precheckData: InvitePrecheckResponse | null =
        precheckResult?.status && precheckResult?.data ? precheckResult.data : precheckResult;

      if (!precheckResponse.ok || !precheckData?.mode) {
        alert(`초대 사전검증 실패: ${precheckResult?.message || precheckData?.message || '오류가 발생했습니다.'}`);
        return;
      }

      if (precheckData.mode === 'ALREADY_PARTNERED') {
        alert(sanitizeInviteMessage(precheckData.message) || '이미 파트너로 연결된 업체입니다.');
        return;
      }

      if (precheckData.mode === 'REVIEW_REQUIRED') {
        alert(
          `${sanitizeInviteMessage(precheckData.message) || '자동 진행이 어려운 상태입니다.'}\n연락처를 다시 확인한 후 다시 시도해주세요.`
        );
        return;
      }

      const confirmableMode = precheckData.mode === 'LINK_EXISTING' || precheckData.mode === 'UPGRADE_LOCAL';
      if (!confirmableMode) {
        alert(sanitizeInviteMessage(precheckData.message) || '확정 가능한 초대 모드가 아닙니다.');
        return;
      }

      if (!precheckData.confirmToken) {
        alert('확정 토큰을 받지 못했습니다. 다시 시도해주세요.');
        return;
      }

      const modeLabel = getInviteModeLabel(precheckData.mode);
      const precheckMessage = sanitizeInviteMessage(precheckData.message) || getPrecheckGuideMessage(precheckData.mode);
      const shouldConfirm = window.confirm(
        `[${modeLabel}]\n${precheckMessage}\n\n이대로 진행할까요?`
      );

      if (!shouldConfirm) return;

      const confirmResponse = await fetch('https://api.wedmin.biz/vendor/invite/confirm', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ confirmToken: precheckData.confirmToken })
      });

      const confirmResult = await confirmResponse.json().catch(() => null);
      const confirmData: InviteConfirmResponse | null =
        confirmResult?.status && confirmResult?.data ? confirmResult.data : confirmResult;

      if (!confirmResponse.ok || !confirmData?.mode) {
        alert(`초대 확정 실패: ${confirmResult?.message || confirmData?.message || '오류가 발생했습니다.'}`);
        return;
      }

      if (confirmData.rollbackToken) {
        setInviteRollbackInfo({
          rollbackToken: confirmData.rollbackToken,
          mode: confirmData.mode,
          message: sanitizeInviteMessage(confirmData.message) || '업체 초대/연결이 완료되었습니다.',
          rollbackExpiresInSeconds: confirmData.rollbackExpiresInSeconds || 1800,
          partnerVendorIdx: confirmData.partnerVendorIdx,
          inviteRequestIdx: precheckData.inviteRequestIdx
        });
      } else {
        setInviteRollbackInfo(null);
      }

      const successMessage = sanitizeInviteMessage(confirmData.message) || '업체 초대/연결이 완료되었습니다.';
      const tempPasswordMessage = confirmData.issuedTempPassword
        ? `\n임시 비밀번호: ${confirmData.issuedTempPassword}`
        : '';
      alert(`${successMessage}${tempPasswordMessage}`);
      setShowInviteModal(false);
      fetchVendors(statusFilter, searchTerm, categoryFilterIdx);
    } catch (error) {
      console.error('Invite vendor error:', error);
      alert('오류가 발생했습니다.');
    } finally {
      setIsInvitingVendor(false);
    }
  };

  const handleRollbackInvite = async () => {
    if (!inviteRollbackInfo?.rollbackToken || isRollingBackInvite) return;
    const shouldRollback = window.confirm('방금 보낸 초대를 취소하시겠습니까?');
    if (!shouldRollback) return;

    try {
      setIsRollingBackInvite(true);
      const token = localStorage.getItem('accessToken');
      const response = await fetch('https://api.wedmin.biz/vendor/invite/cancel', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ rollbackToken: inviteRollbackInfo.rollbackToken })
      });

      const result = await response.json().catch(() => null);
      const message = sanitizeInviteMessage(result?.data?.message || result?.message) || '초대 취소가 완료되었습니다.';
      const actionType: string | undefined = result?.data?.actionType || result?.actionType;
      const actionTypeLabel =
        actionType === 'UPGRADE_LOCAL'
          ? getInviteModeLabel('UPGRADE_LOCAL')
          : actionType === 'LINK_EXISTING'
            ? getInviteModeLabel('LINK_EXISTING')
            : '';

      if (!response.ok || result?.status === false) {
        alert(`롤백 실패: ${result?.message || '오류가 발생했습니다.'}`);
        return;
      }

      alert(actionTypeLabel ? `${message}\n처리 유형: ${actionTypeLabel}` : message);
      setInviteRollbackInfo(null);
      fetchVendors(statusFilter, searchTerm, categoryFilterIdx);
    } catch (error) {
      console.error('Rollback invite error:', error);
      alert('롤백 처리 중 오류가 발생했습니다.');
    } finally {
      setIsRollingBackInvite(false);
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 p-8 overflow-auto">
        {/* 헤더 */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">업체관리</h1>
            <p className="text-sm text-gray-600 mt-1">거래처 정보를 체계적으로 관리하세요</p>
          </div>
          <div className="flex gap-3">
            {/* <button onClick={() => setShowSearchModal(true)} ...> WedlogBiz 업체 검색 </button> */}
            <button 
              onClick={() => {
                setSelectedWedlogVendor(null);
                setShowAddForm(true);
              }}
              className="bg-blue-600 text-white px-6 py-2.5 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap flex items-center gap-2 cursor-pointer"
            >
              <i className="ri-add-line"></i>업체 등록
            </button>
          </div>
        </div>
        {/* 검색 및 필터 */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="space-y-3">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex-1 min-w-64">
                <div className="relative">
                  <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 flex items-center justify-center"></i>
                  <input
                    type="text"
                    placeholder="업체명, 담당자, 연락처로 검색..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleSelectAllCategories}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer ${categoryFilterIdx === null ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  전체
                </button>
                {parentCategoryOptions.map((category) => (
                  <button
                    key={category.value}
                    onClick={() => handleSelectParentCategory(category.value)}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer ${selectedParentCategoryIdx === category.value ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                  >
                    {category.label}
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                {statusOptions.map((status) => (
                  <button
                    key={status}
                    onClick={() => setStatusFilter(status)}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer ${statusFilter === status ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                  >
                    {status}
                  </button>
                ))}
              </div>
            </div>

            {selectedParentCategoryIdx && childCategoryOptions.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium text-gray-500 mr-1">세부 분류</span>
                <button
                  onClick={() => handleSelectChildCategory(null)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer ${categoryFilterIdx === selectedParentCategoryIdx ? 'bg-blue-100 text-blue-700 border border-blue-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200'}`}
                >
                  전체
                </button>
                {childCategoryOptions.map((child) => (
                  <button
                    key={child.vendorCategoryIdx}
                    onClick={() => handleSelectChildCategory(child.vendorCategoryIdx)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer ${categoryFilterIdx === child.vendorCategoryIdx ? 'bg-blue-100 text-blue-700 border border-blue-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200'}`}
                  >
                    {child.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* 업체 목록 */}
        {isLoading ? (
          <div className="flex justify-center p-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="space-y-4">
            {vendors.map((vendor) => {
              const relationBadge = getVendorRelationBadge(vendor);
              return (
              <div 
                key={vendor.id} 
                className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/dashboard/vendor-detail?id=${vendor.id}`)}
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                      <i className="ri-building-line text-2xl text-blue-600"></i>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-xl font-bold text-gray-800">{vendor.name}</h3>
                        <span className={`inline-flex px-3 py-1 text-xs font-medium rounded-full ${vendor.status === '활성' ? 'bg-green-100 text-green-700' : vendor.status === '대기' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                          {vendor.status}
                        </span>
                        <span className={`inline-flex px-3 py-1 text-xs font-medium rounded-full ${relationBadge.className}`}>
                          {relationBadge.label}
                        </span>
                        <span className="inline-flex px-3 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">{vendor.category}</span>
                        {vendor.subCategory && <span className="inline-flex px-3 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-700">{vendor.subCategory}</span>}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <div className="flex items-center gap-1"><i className="ri-user-line w-4 h-4 flex items-center justify-center"></i><span>담당: {vendor.manager}</span></div>
                        <div className="flex items-center gap-1"><i className="ri-phone-line w-4 h-4 flex items-center justify-center"></i><span>{vendor.contact}</span></div>
                        <div className="flex items-center gap-1"><i className="ri-mail-line w-4 h-4 flex items-center justify-center"></i><span>{vendor.email}</span></div>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {!vendor.isPartnership && vendor.canInvite && (
                      <button
                        onClick={(e) => handleOpenInviteModal(vendor, e)}
                        className="text-blue-600 hover:text-blue-800 px-3 h-10 flex items-center justify-center hover:bg-blue-50 rounded transition-colors cursor-pointer text-sm"
                      >
                        업체 초대하기
                      </button>
                    )}
                    {vendor.isPartnership ? (
                      <>
                        {isRollbackTargetVendor(vendor.id) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRollbackInvite();
                            }}
                            disabled={isRollingBackInvite}
                            className={`px-3 h-10 flex items-center justify-center rounded transition-colors cursor-pointer text-sm ${
                              isRollingBackInvite
                                ? 'text-amber-400 bg-amber-100 cursor-not-allowed'
                                : 'text-amber-700 hover:text-amber-900 hover:bg-amber-50'
                            }`}
                          >
                            {isRollingBackInvite ? '취소 중...' : '초대 취소'}
                          </button>
                        )}
                        <button
                          onClick={(e) => handleRelationshipStatusChange(vendor, e)}
                          className="text-green-700 hover:text-green-900 px-3 h-10 flex items-center justify-center hover:bg-green-50 rounded transition-colors cursor-pointer text-sm"
                        >
                          상태 변경
                        </button>
                        <button
                          onClick={(e) => handleVendorDelete(vendor, e)}
                          className="text-red-600 hover:text-red-800 px-3 h-10 flex items-center justify-center hover:bg-red-50 rounded transition-colors cursor-pointer text-sm"
                        >
                          연결 해제
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/dashboard/vendor-detail?id=${vendor.id}`);
                          }}
                          className="text-indigo-700 hover:text-indigo-900 px-3 h-10 flex items-center justify-center hover:bg-indigo-50 rounded transition-colors cursor-pointer text-sm"
                        >
                          정보 수정
                        </button>
                        <button 
                          onClick={(e) => handleVendorDelete(vendor, e)}
                          className="text-red-600 hover:text-red-800 w-10 h-10 flex items-center justify-center hover:bg-red-50 rounded transition-colors cursor-pointer"
                          title="삭제"
                        >
                          <i className="ri-delete-bin-line text-xl"></i>
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {/* (그리드 상세 정보 및 하단 정보 - 기존 코드 유지) */}
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="bg-gray-50 rounded-lg p-3"><div className="text-xs text-gray-500 mb-1">사업자번호</div><div className="text-sm font-medium text-gray-800">{vendor.businessNumber}</div></div>
                  <div className="bg-gray-50 rounded-lg p-3"><div className="text-xs text-gray-500 mb-1">등록일</div><div className="text-sm font-medium text-gray-800">{vendor.createdDt}</div></div>
                  <div className="bg-gray-50 rounded-lg p-3"><div className="text-xs text-gray-500 mb-1">상태</div><div className={`text-sm font-medium ${vendor.status === '활성' ? 'text-green-600' : 'text-red-600'}`}>{vendor.status}</div></div>
                </div>
                <div className="flex items-center justify-between text-sm text-gray-600">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1"><i className="ri-map-pin-line w-4 h-4 flex items-center justify-center"></i><span>{vendor.address}</span></div>
                    {vendor.memo && <div className="flex items-center gap-1"><i className="ri-sticky-note-line w-4 h-4 flex items-center justify-center"></i><span>{vendor.memo}</span></div>}
                  </div>
                  <div className="text-blue-600 font-medium">상세보기 →</div>
                </div>
              </div>
              );
            })}
            {inviteRollbackInfo && (
              <div className="flex flex-wrap items-center gap-3 px-1">
                <p className="text-xs text-amber-700">
                  초대 취소는 방금 초대한 업체에서만 가능하며, 약 {rollbackAvailableMinutes}분 뒤 자동으로 만료됩니다.
                </p>
                {!isRollbackTargetVisible && (
                  <button
                    type="button"
                    onClick={handleRollbackInvite}
                    disabled={isRollingBackInvite}
                    className={`px-3 py-1 text-xs rounded border ${
                      isRollingBackInvite
                        ? 'text-amber-400 border-amber-200 bg-amber-50 cursor-not-allowed'
                        : 'text-amber-700 border-amber-300 hover:bg-amber-50'
                    }`}
                  >
                    {isRollingBackInvite ? '취소 중...' : '초대 취소'}
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {!isLoading && vendors.length === 0 && (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <i className="ri-building-line text-4xl text-gray-300 mb-4"></i>
            <p className="text-gray-500">검색 조건에 맞는 업체가 없습니다.</p>
          </div>
        )}

        {/* Modals */}
        <VendorSearchModal
          isOpen={showSearchModal}
          onClose={() => setShowSearchModal(false)}
          onSelect={(vendor) => {
            setSelectedWedlogVendor(vendor);
            setShowSearchModal(false);
            setShowAddForm(true);
          }}
        />

        <VendorFormModal
          isOpen={showAddForm}
          onClose={() => {
            setShowAddForm(false);
            setSelectedWedlogVendor(null);
          }}
          preSelectedVendor={selectedWedlogVendor}
          categories={categories}
          categoryList={categoryList}
          onSubmit={handleVendorCreateSuccess}
          onClearSelection={() => setSelectedWedlogVendor(null)}
        />

        {showInviteModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">업체 초대</h2>
                <button onClick={() => setShowInviteModal(false)} className="text-gray-400 hover:text-gray-600">
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              <form onSubmit={handleInviteVendor} className="p-6 space-y-6">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">기본 정보</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">업체명 *</label>
                      <input
                        type="text"
                        required
                        value={inviteFormData.name}
                        readOnly
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">카테고리</label>
                      <input
                        type="text"
                        value={inviteFormData.categoryName}
                        readOnly
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">연락처 정보</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">대표 연락처 *</label>
                      <input
                        type="tel"
                        required
                        value={inviteFormData.contact}
                        onChange={(e) =>
                          setInviteFormData((prev) => ({ ...prev, contact: formatMobile(e.target.value) }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="010-1234-5678"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        010으로 시작하는 문자 수신 가능한 번호만 등록할 수 있습니다.
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">담당자 성함 (선택)</label>
                      <input
                        type="text"
                        value={inviteFormData.managerName}
                        onChange={(e) => setInviteFormData((prev) => ({ ...prev, managerName: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="홍길동"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm text-gray-600 mb-1">이메일 (계정용, 선택)</label>
                      <input
                        type="email"
                        value={inviteFormData.email}
                        onChange={(e) => setInviteFormData((prev) => ({ ...prev, email: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="vendor@example.com"
                      />
                    </div>
                  </div>
                </div>

                <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-700">
                  초대 확정 후 계정 정보가 발급되며, 환경 설정에 따라 임시 비밀번호가 함께 전달될 수 있습니다.
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">사업자 정보</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">사업자등록번호</label>
                      <input
                        type="text"
                        value={inviteFormData.businessNumber}
                        readOnly
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">사업장 주소</label>
                      <input
                        type="text"
                        value={inviteFormData.address}
                        readOnly
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700"
                      />
                    </div>
                  </div>
                </div>

                <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3 -mx-6 -mb-6">
                  <button type="button" onClick={() => setShowInviteModal(false)} className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg">
                    취소
                  </button>
                  <button
                    type="submit"
                    disabled={isInvitingVendor}
                    className={`px-6 py-2 rounded-lg text-white ${
                      isInvitingVendor ? 'bg-blue-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                  >
                    {isInvitingVendor ? '처리 중...' : '초대 발송'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
